import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useI18n } from "@wato/i18n/react";
import { cx } from "../../cx";
import { AlertCircleIcon, CheckCircleIcon, CloseIcon, InfoCircleIcon } from "../../rtl/icons";

export type ToastTone = "success" | "error" | "info";

/** Tone colours — semantic tokens only (pairs verified by test/contrast.test.ts). */
export const toastToneClasses: Readonly<Record<ToastTone, { box: string; icon: string }>> = {
  success: { box: "bg-success-bg text-success-fg border-success-icon", icon: "text-success-icon" },
  error: { box: "bg-danger-bg text-danger-bg-fg border-danger-icon", icon: "text-danger-icon" },
  info: { box: "bg-info-bg text-info-fg border-info-icon", icon: "text-info-icon" },
};

const ICON: Readonly<Record<ToastTone, typeof InfoCircleIcon>> = {
  success: CheckCircleIcon,
  error: AlertCircleIcon,
  info: InfoCircleIcon,
};

export interface ToastProps {
  readonly tone?: ToastTone;
  readonly title: string;
  readonly description?: string;
  /** Optional link-style action (e.g. a deep link) rendered under the text. */
  readonly action?: { readonly label: string; readonly href: string };
  readonly onDismiss?: () => void;
  readonly className?: string;
}

/**
 * A single notification. Errors use `role="alert"` (assertive); success/info use
 * `role="status"` (polite). The accent border sits on the inline-start edge.
 */
export function Toast({
  tone = "info",
  title,
  description,
  action,
  onDismiss,
  className,
}: ToastProps) {
  const { t } = useI18n();
  const Icon = ICON[tone];
  return (
    <div
      role={tone === "error" ? "alert" : "status"}
      className={cx(
        "pointer-events-auto flex w-full items-start gap-3 rounded-lg border border-s-4 p-3 shadow-md sm:p-4",
        toastToneClasses[tone].box,
        className,
      )}
    >
      <Icon className={cx("mt-0.5", toastToneClasses[tone].icon)} />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="text-sm font-semibold">{title}</p>
        {description && <p className="text-sm">{description}</p>}
        {action && (
          <a href={action.href} className="text-sm font-semibold underline">
            {action.label}
          </a>
        )}
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={t("ui.toast.dismiss")}
          className="-me-1 -mt-1 flex size-7 shrink-0 items-center justify-center rounded-sm hover:bg-surface-sunken"
        >
          <CloseIcon className="size-4" />
        </button>
      )}
    </div>
  );
}

export interface ToastOptions {
  readonly tone?: ToastTone;
  readonly title: string;
  readonly description?: string;
  readonly action?: { readonly label: string; readonly href: string };
  /** Milliseconds before auto-dismiss; `0` keeps it until dismissed. */
  readonly duration?: number;
  /** OWN-378: called only when the person presses ✕ (not on auto-dismiss). */
  readonly onDismiss?: () => void;
}

interface ActiveToast extends ToastOptions {
  readonly id: string;
}

export interface ToastApi {
  show(options: ToastOptions): string;
  dismiss(id: string): void;
  dismissAll(): void;
}

/** localStorage key of the per-user "hide pop-up alerts" preference. */
export const HIDE_INFO_TOASTS_KEY = "wato.ui.hideInfoToasts";
/** Identical titles inside this window replace the older toast instead of stacking. */
const DEDUPE_MS = 30_000;
/** Small screens (below the sm breakpoint) show at most this many toasts. */
const SMALL_SCREEN_LIMIT = 2;

export function getHideInfoToasts(): boolean {
  try {
    return window.localStorage.getItem(HIDE_INFO_TOASTS_KEY) === "1";
  } catch {
    return false;
  }
}

export function setHideInfoToasts(hide: boolean): void {
  try {
    if (hide) window.localStorage.setItem(HIDE_INFO_TOASTS_KEY, "1");
    else window.localStorage.removeItem(HIDE_INFO_TOASTS_KEY);
  } catch {
    /* storage unavailable: the preference simply does not persist */
  }
}

/** Tailwind `sm` breakpoint in px; below it toasts stack full-width. */
const SM_BREAKPOINT = 640;

function isSmallScreen(): boolean {
  try {
    return (
      typeof window.matchMedia === "function" &&
      !window.matchMedia(`(min-width: ${SM_BREAKPOINT}px)`).matches
    );
  } catch {
    return false;
  }
}

const ToastContext = createContext<ToastApi | null>(null);

export interface ToastProviderProps {
  readonly children: ReactNode;
  /** Default auto-dismiss delay in ms. */
  readonly duration?: number;
  /** Maximum toasts on screen; the oldest is dropped first. */
  readonly limit?: number;
  /**
   * Show at most this many toasts at once and queue the rest (shown, in order, as the visible
   * ones are dismissed or time out). Unset keeps the `limit` behaviour (oldest dropped first).
   */
  readonly maxVisible?: number;
}

/** Hosts the toast stack at the bottom inline-end corner (bottom-left in Arabic). */
export function ToastProvider({
  children,
  duration = 5000,
  limit = 4,
  maxVisible,
}: ToastProviderProps) {
  const { t } = useI18n();
  const [toasts, setToasts] = useState<readonly ActiveToast[]>([]);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const counter = useRef(0);
  const recent = useRef(new Map<string, number>());
  const queued = maxVisible !== undefined;

  const dismiss = useCallback((id: string) => {
    const timer = timers.current.get(id);
    if (timer) clearTimeout(timer);
    timers.current.delete(id);
    setToasts((list) => list.filter((toast) => toast.id !== id));
  }, []);

  const dismissAll = useCallback(() => {
    for (const timer of timers.current.values()) clearTimeout(timer);
    timers.current.clear();
    setToasts([]);
  }, []);

  const show = useCallback(
    (options: ToastOptions) => {
      counter.current += 1;
      const id = `toast-${counter.current}`;
      if ((options.tone ?? "info") === "info" && getHideInfoToasts()) return id;
      const now = Date.now();
      const last = recent.current.get(options.title);
      const duplicate = last !== undefined && now - last < DEDUPE_MS;
      recent.current.set(options.title, now);
      const cap = isSmallScreen() ? Math.min(limit, SMALL_SCREEN_LIMIT) : limit;
      setToasts((list) => {
        if (duplicate)
          for (const old of list)
            if (old.title === options.title) {
              const timer = timers.current.get(old.id);
              if (timer) clearTimeout(timer);
              timers.current.delete(old.id);
            }
        const kept = duplicate ? list.filter((x) => x.title !== options.title) : list;
        const next = [...kept, { ...options, id }];
        return queued ? next : next.slice(-cap);
      });
      // Queued toasts start their timer when they become visible (effect below).
      const ms = options.duration ?? duration;
      if (!queued && ms > 0)
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), ms),
        );
      return id;
    },
    [dismiss, duration, limit, queued],
  );

  const visible = queued ? toasts.slice(0, maxVisible) : toasts;
  useEffect(() => {
    if (!queued) return;
    for (const toast of visible) {
      const ms = toast.duration ?? duration;
      if (ms > 0 && !timers.current.has(toast.id))
        timers.current.set(
          toast.id,
          setTimeout(() => dismiss(toast.id), ms),
        );
    }
  }, [queued, visible, duration, dismiss]);

  useEffect(() => {
    const map = timers.current;
    return () => {
      for (const timer of map.values()) clearTimeout(timer);
      map.clear();
    };
  }, []);

  const api = useMemo<ToastApi>(() => ({ show, dismiss, dismissAll }), [show, dismiss, dismissAll]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <section
        aria-label={t("ui.toast.region")}
        className="pointer-events-none fixed start-4 end-4 bottom-4 z-50 flex flex-col gap-2 sm:start-auto sm:w-full sm:max-w-[var(--size-toast)]"
      >
        {visible.length >= 2 && (
          <button
            type="button"
            onClick={dismissAll}
            className="pointer-events-auto self-end rounded-md border border-line bg-surface px-3 py-1 text-xs font-semibold shadow-sm hover:bg-surface-sunken"
          >
            {t("ui.toast.dismissAll")}
          </button>
        )}
        {visible.map((toast) => (
          <Toast
            key={toast.id}
            tone={toast.tone ?? "info"}
            title={toast.title}
            {...(toast.description ? { description: toast.description } : {})}
            {...(toast.action ? { action: toast.action } : {})}
            onDismiss={() => {
              toast.onDismiss?.();
              dismiss(toast.id);
            }}
          />
        ))}
      </section>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error("useToast must be used inside <ToastProvider>");
  return api;
}
